import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import Axios from '../../../config/axios'
import { getUserFromLocalStorage } from '../../../utils/utils';
import { UrlConstant } from '../../../commom/UrlConstant';
import { useDocumentPassword } from '../../../commom/hooks/useDocumentPassword';

interface MonthlyStatementDialogProps {
  open: boolean;
  onClose: () => void;
}

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

const selectSx = {
  color: '#fff',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff30' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#F2674A' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#F2674A' },
  '& .MuiSvgIcon-root': { color: '#aaa' },
};

const menuPropsSx = {
  PaperProps: {
    sx: {
      bgcolor: '#2a2a2a',
      '& .MuiMenuItem-root': { color: '#fff' },
      '& .MuiMenuItem-root:hover': { bgcolor: '#F2674A22' },
      '& .MuiMenuItem-root.Mui-selected': { bgcolor: '#F2674A33' },
      '& .MuiMenuItem-root.Mui-selected:hover': { bgcolor: '#F2674A44' },
    }
  }
};

const MonthlyStatementDialog: React.FC<MonthlyStatementDialogProps> = ({ open, onClose }) => {
  const [month, setMonth] = useState<number>(currentMonth);
  const [year, setYear] = useState<number>(currentYear);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { fetchPassword } = useDocumentPassword();
  const [successMsg, setSuccessMsg] = useState('');



  const user = getUserFromLocalStorage();

  const handleDownload = async () => {
    setError('');
    setSuccessMsg('');
    setLoading(true);
    try {
      const response = await Axios.get(UrlConstant.User.GetMonthlyStatement, {
        params: { id: user?._id, month, year },
        responseType: 'blob',
      });

      const monthLabel = MONTHS.find(m => m.value === month)?.label;
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Statement_${monthLabel}_${year}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      const pwd = await fetchPassword();
      if (pwd) {
        // Give the user a moment to read the password before the dialog closes
        setSuccessMsg(`Downloaded! Your PDF password: ${pwd}`);
        setTimeout(onClose, 4000);
      } else {
        onClose();
      }
    } catch {
      setError('Failed to download statement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // prevent selecting future month in current year
  const availableMonths = MONTHS.filter(m =>
    year < currentYear ? true : m.value <= currentMonth
  );

  const handleYearChange = (e: SelectChangeEvent<number>) => {
    const newYear = Number(e.target.value);
    setYear(newYear);
    if (newYear === currentYear && month > currentMonth) {
      setMonth(currentMonth);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      PaperProps={{
        sx: {
          bgcolor: '#1a1a1a',
          borderRadius: '16px',
          minWidth: '380px',
          border: '1px solid #ffffff1a',
        }
      }}
    >
      <DialogTitle sx={{ color: '#fff', fontSize: '20px', fontWeight: 600, pb: 1 }}>
        Monthly Statement
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ color: '#aaa', fontSize: '14px', mb: 3 }}>
          Select the month and year to download your statement.
        </Typography>

        <Box display="flex" gap="16px">
          {/* Month */}
          <FormControl fullWidth>
            <InputLabel shrink={false} sx={{ color: '#aaaa !important', '&.Mui-focused': { color: '#F2674A' } }}>
              Month
            </InputLabel>
            <Select
              value={month}
              //   label="Month"
              onChange={(e) => setMonth(Number(e.target.value))}
              sx={selectSx}
              MenuProps={menuPropsSx}
            >
              {availableMonths.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Year */}
          <FormControl fullWidth>
            <InputLabel shrink={false} sx={{ color: '#aaaa !important', '&.Mui-focused': { color: '#F2674A' } }}>
              Year
            </InputLabel>
            <Select
              value={year}
              //   label="Year"
              onChange={handleYearChange}
              sx={selectSx}
              MenuProps={menuPropsSx}
            >
              {YEARS.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Typography sx={{ color: '#f44336', fontSize: '13px', mt: 2 }}>
            {error}
          </Typography>
        )}

        {successMsg && (
          <Typography sx={{ color: '#4CAF50', fontSize: '13px', mt: 2 }}>
            {successMsg}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            color: '#aaa', border: '1px solid #ffffff30', borderRadius: '8px',
            px: 3, '&:hover': { borderColor: '#fff', color: '#fff' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDownload}
          disabled={loading}
          className="btn btn-primary"
          sx={{ px: 3, borderRadius: '8px', minWidth: '140px' }}
        >
          {loading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} sx={{ color: '#fff' }} />
              <span>Downloading...</span>
            </Box>
          ) : 'Download PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MonthlyStatementDialog;